import isEmpty from 'lodash/isEmpty';
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
  UseMutationOptions,
  keepPreviousData,
} from '@tanstack/react-query';
import queryString from 'query-string';
import { User, Skill } from '@gloat/types';
import { eventDispatcher } from '@gloat/services';
import { CAREER_PATH_KEY } from '../pages/path/path-pages/career-planning/career-path/queries/use-paths';
import { apiAuthClient } from '../root/api-auth-client';
import { userProfileCompletenessQueryKey } from './user-profile-completeness';

export const userQueryKey = 'user';

const useUser = (argu?: { enabled?: boolean }): UseQueryResult<User> => {
  const { enabled } = argu || {};
  return useQuery<User, Error>({
    queryKey: [userQueryKey],

    queryFn: async () => {
      const res = await apiAuthClient.fetch('/api/v1/auth/user');

      const user = await res.json();
      const csrfToken = user?.csrfToken;
      localStorage.setItem('csrfToken', csrfToken);

      if (!res.ok) {
        throw new Error(`Failed to fetch user - network response was not ok ${res.status}`);
      }

      user.fullName =
        user?.fullName ||
        (user?.firstName || user?.lastName ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() : undefined);

      return user;
    },

    enabled,
    retry: false,
  });
};

// payload that comes back from backend after the mutation
type UserMutationResponse = User & { error?: string; payload?: { userMessage?: string } };

// possible body of the request we send to backend
// sometime we send profile image with id in case of presigned url update and sometimes we send null in case of deletion
interface UserMutationPayloadThatIsSentToTheServer extends Partial<User> {
  profileImage:
    | (User['profileImage'] & {
        id?: string;
      })
    | null;
}

const useUserMutation = (
  options?: UseMutationOptions<UserMutationResponse, any, Partial<UserMutationPayloadThatIsSentToTheServer>>,
): UseMutationResult<UserMutationResponse, any, Partial<UserMutationPayloadThatIsSentToTheServer>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async payload => {
      const res = await apiAuthClient.fetch('/api/v1/auth/user', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(`Failed to update user`);
      }
      return await res.json();
    },

    ...(options || {}),

    onSuccess: (data, ...args) => {
      if (!data.error && !isEmpty(data)) {
        queryClient.setQueryData([userQueryKey], data);
      }
      // invalidating career paths to fetch affected skills
      queryClient.invalidateQueries({
        queryKey: [CAREER_PATH_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [userProfileCompletenessQueryKey],
      });

      options?.onSuccess?.(data, ...args);
    },
  });
};

const useAcceptTermsMutation = (): UseMutationResult<User, Error, User> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await apiAuthClient.fetch('/api/v1/user/welcome', {
        method: 'PUT',
        body: JSON.stringify({ seen: { termsOfService: true } }),
      });
      if (!res.ok) {
        throw new Error(`Network response was not ok ${res.status}`);
      }
      return await res.json();
    },

    onSuccess: response => {
      eventDispatcher.event({ name: 'terms-of-service-accepted' });
      queryClient.setQueryData<User>([userQueryKey], user => {
        return { ...user, seen: { ...user?.seen, termsOfService: response?.seen?.termsOfService } };
      });
    },
  });
};

// this action saves record video - it receives file name and BE know to copy that file from the 3rd party provider
// bucket to our owns buckets and will return the new video url in the payload.
const useRecordUserVideo = (): UseMutationResult<string, Error> => {
  return useMutation({
    mutationFn: async (input: any) => {
      const videoId = `${input}.mp4`;
      const res = await apiAuthClient.fetch(`/api/v1/cdn/move-video`, {
        method: 'POST',
        body: JSON.stringify({
          asset_id: videoId,
        }),
      });
      if (res.ok) {
        return videoId;
      } else {
        throw new Error('failed to record video');
      }
    },
  });
};

const SUGGESTED_SKILLS_QUERY_KEY = 'SKILLS_SUGGESTIONS';

export interface SkillsSuggestionsResponse {
  market: Skill[];
  organization: Skill[];
}

const useUserSkillsSuggestions = ({
  userTitle,
  employee,
  skills,
  enabled,
}: {
  skills?: Skill[];
  userTitle?: string;
  employee?: number;
  enabled?: boolean;
} = {}): UseQueryResult<SkillsSuggestionsResponse, Error> => {
  const { data: user } = useUser();
  const title = userTitle || user?.title;
  const query = { skills: skills?.map(skill => skill.id), title, employee, scope: 'user' };

  return useQuery({
    queryKey: [SUGGESTED_SKILLS_QUERY_KEY, query],

    queryFn: async () => {
      const res = await apiAuthClient.fetch(`/api/v1/tags/suggest?${queryString.stringify(query)}`);

      if (!res.ok) {
        throw new Error('Failed to fetch suggested skills');
      }

      return await res.json();
    },

    staleTime: 0,
    placeholderData: keepPreviousData,
    enabled,
  });
};

type ManagedUsersResponse = {
  has_next: boolean;
  items: User[];
  page: number;
  page_count: number;
  size: number;
  total_count: number;
};

async function fetchManagedUsers(userId?: number): Promise<ManagedUsersResponse> {
  const res = await apiAuthClient.fetch(`/api/user-service/v1/users?direct_manager_id=${userId}`);

  if (!res.ok) {
    throw new Error('Error in fetching managed users');
  }

  return res.json();
}

function useGetManagedUsers(userId?: number) {
  return useQuery({
    queryKey: [userQueryKey, 'managed-users', userId],
    queryFn: () => fetchManagedUsers(userId),
    enabled: !!userId,
  });
}

export {
  userQueryKey as key,
  useUserMutation,
  useRecordUserVideo,
  useAcceptTermsMutation,
  useUserSkillsSuggestions,
  useGetManagedUsers,
  SUGGESTED_SKILLS_QUERY_KEY,
};
export default useUser;
